import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { client } from '../api/Api';

const useUploadHandler = (
    showSnackbar: (message: string, severity: 'success' | 'error') => void,
    setReceiptData: (data: any) => void,
    setReceiptModalOpen: (open: boolean) => void
) => {
    const navigate = useNavigate();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadStatus, setUploadStatus] = useState<'uploading' | 'error' | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const uploadFileToServer = async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            setUploadStatus('uploading');
            setUploadProgress(0);
            setUploadError(null);

            const response = await client.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                    setUploadProgress(progress);
                },
            });

            // Check if upload was successful
            if (response.data?.success) {
                showSnackbar('Kvitas įkeltas. Apdorojimas vyksta fone.', 'success');
                
                // Redirect to account page with messages tab after short delay
                setTimeout(() => {
                    navigate('/account?tab=messages');
                }, 1000);
            } else {
                throw new Error(response.data?.message || 'Upload failed');
            }

            setTimeout(() => {
                setUploadStatus(null);
                setUploadProgress(0);
            }, 3000);
        } catch (error) {
            setUploadStatus('error');
            const errorMessage = error.response?.data?.message || 'Upload failed. Please try again.';
            setUploadError(errorMessage);
            showSnackbar(errorMessage, 'error');
            console.error('Upload error:', error);

            setTimeout(() => {
                setUploadStatus(null);
                setUploadProgress(0);
                setUploadError(null);
            }, 5000);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            console.log('Selected file:', file);

            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                showSnackbar('File size must be less than 10MB', 'error');
                return;
            }

            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                showSnackbar('File type not supported', 'error');
                return;
            }

            uploadFileToServer(file);
        }

        event.target.value = '';
    };

    return {
        selectedFile,
        uploadStatus,
        uploadProgress,
        uploadError,
        handleFileSelect,
        uploadFileToServer,
    };
};

export default useUploadHandler;